import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Car, Phone, Clock, Shield, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { DEFAULT_DURATION_MINUTES, MIN_DURATION_MINUTES, DURATION_OPTIONS } from '@/constants';

const VisitorCheckIn = () => {
  const { society_code } = useParams();
  const [step, setStep] = useState(1); // 1=phone, 2=otp, 3=vehicle+duration, 4=payment, 5=done
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [societyInfo, setSocietyInfo] = useState(null);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [mockOtp, setMockOtp] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [duration, setDuration] = useState(DEFAULT_DURATION_MINUTES);
  const [bookingData, setBookingData] = useState(null);
  const [passData, setPassData] = useState(null);

  const fetchInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/visitor-checkin/${society_code}/info`);
      setSocietyInfo(res.data);
    } catch {
      toast.error('Society not found');
    } finally { setLoading(false); }
  }, [society_code]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleSendOTP = async () => {
    if (!phone || phone.length < 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/visitor-checkin/${society_code}/send-otp`, { visitor_phone: phone });
      setMockOtp(res.data.otp);
      toast.success(`OTP sent: ${res.data.otp} (mock)`);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally { setSubmitting(false); }
  };

  const handleVerifyAndBook = async () => {
    if (!otp) { toast.error('Enter the OTP'); return; }
    if (!vehicleNumber) { toast.error('Enter your vehicle number'); return; }
    if (!duration || duration < MIN_DURATION_MINUTES) { toast.error(`Minimum duration is ${MIN_DURATION_MINUTES} minutes`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/visitor-checkin/${society_code}/verify-and-book`, {
        visitor_phone: phone, otp, vehicle_number: vehicleNumber, duration_minutes: parseInt(duration)
      });
      setBookingData(res.data);
      if (res.data.is_free) {
        // Free spot - skip payment, go to done
        setPassData({ spot_number: res.data.spot_number, booking_id: res.data.booking_id });
        setStep(5);
        toast.success('FREE spot allocated! No payment needed.');
      } else {
        setStep(4);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally { setSubmitting(false); }
  };

  const handleConfirmPayment = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/visitor-checkin/${society_code}/confirm-payment/${bookingData.booking_id}`);
      setPassData(res.data);
      setStep(5);
      toast.success('Payment confirmed! Spot allocated.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
      </div>
    );
  }

  if (!societyInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F4F4F5' }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#DC2626' }}>Society Not Found</h1>
          <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>This QR code may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  const amount = (parseInt(duration || 0) / 60) * societyInfo.rate_per_hour;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      {/* Header */}
      <div className="py-4 px-6" style={{ backgroundColor: '#09090B' }}>
        <div className="max-w-lg mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#FBBF24', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            ParkShare
          </p>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }}>
            {societyInfo.society_name}
          </h1>
          <p className="text-xs" style={{ color: '#A1A1AA', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Visitor Self Check-In
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-lg mx-auto px-6 py-4">
        <div className="flex items-center gap-1 mb-6">
          {['Phone', 'OTP', 'Details', 'Pay', 'Done'].map((label, i) => (
            <div key={label} className="flex-1 flex flex-col items-center">
              <div
                className="w-full h-1.5 mb-1"
                style={{ backgroundColor: i + 1 <= step ? '#09090B' : '#E4E4E7' }}
              />
              <span className="text-[10px] font-bold" style={{
                color: i + 1 <= step ? '#09090B' : '#A1A1AA',
                fontFamily: 'IBM Plex Sans, sans-serif'
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Availability Banner */}
        {step < 4 && (
          <div
            className="p-3 mb-6 flex items-center justify-between"
            style={{ backgroundColor: societyInfo.available_spots > 0 ? '#D1FAE5' : '#FEE2E2' }}
            data-testid="availability-banner"
          >
            <div className="flex items-center gap-2">
              <Car size={16} style={{ color: societyInfo.available_spots > 0 ? '#065F46' : '#991B1B' }} />
              <span className="text-sm font-bold" style={{
                fontFamily: 'IBM Plex Sans, sans-serif',
                color: societyInfo.available_spots > 0 ? '#065F46' : '#991B1B'
              }}>
                {societyInfo.available_spots > 0
                  ? `${societyInfo.available_spots} spot${societyInfo.available_spots > 1 ? 's' : ''} available`
                  : 'No spots available right now'}
              </span>
            </div>
            <span className="text-xs font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#71717A' }}>
              ₹{societyInfo.rate_per_hour}/hr
            </span>
          </div>
        )}

        {/* Step 1: Phone */}
        {step === 1 && (
          <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="step-phone">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
                <Phone size={18} style={{ color: '#FBBF24' }} />
              </div>
              <div>
                <h2 className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                  Enter Your Phone
                </h2>
                <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                  We'll send an OTP for verification
                </p>
              </div>
            </div>
            <div className="mb-4">
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>Phone Number</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={10}
                className="mt-1.5 border-2 focus:border-black p-3"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '18px' }}
                data-testid="visitor-phone-input"
              />
            </div>
            <Button
              onClick={handleSendOTP}
              disabled={submitting || societyInfo.available_spots === 0}
              className="w-full p-4"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
              data-testid="send-otp-btn"
            >
              {submitting ? 'Sending...' : 'Send OTP'}
              {!submitting && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="step-otp">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
                <Shield size={18} style={{ color: '#FBBF24' }} />
              </div>
              <div>
                <h2 className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                  Verify OTP
                </h2>
                <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                  Sent to {phone}
                </p>
              </div>
            </div>

            {mockOtp && (
              <div className="p-3 border text-center mb-4" style={{ backgroundColor: '#FEF3C7', borderColor: '#F59E0B' }} data-testid="mock-otp-display">
                <p className="text-xs" style={{ color: '#92400E', fontFamily: 'IBM Plex Sans, sans-serif' }}>Mock OTP (for testing)</p>
                <p className="text-3xl font-black tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{mockOtp}</p>
              </div>
            )}

            <div className="mb-4">
              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>Enter OTP</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                className="mt-1.5 border-2 focus:border-black p-3 text-center"
                style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '24px', letterSpacing: '0.3em' }}
                data-testid="visitor-otp-input"
              />
            </div>
            <Button
              onClick={() => { if (!otp) { toast.error('Enter the OTP'); return; } setStep(3); }}
              className="w-full p-4"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
              data-testid="verify-otp-btn"
            >
              Verify & Continue
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Vehicle + Duration */}
        {step === 3 && (
          <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="step-details">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
                <Car size={18} style={{ color: '#FBBF24' }} />
              </div>
              <div>
                <h2 className="font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
                  Vehicle Details
                </h2>
                <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                  Enter your vehicle number and parking duration
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>Vehicle Number</Label>
                <Input
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                  className="mt-1.5 border-2 focus:border-black p-3"
                  style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '16px' }}
                  data-testid="vehicle-number-input"
                />
              </div>
              <div>
                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3F3F46' }}>Duration (minutes)</Label>
                <div className="flex gap-2 mt-1.5">
                  {DURATION_OPTIONS.map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setDuration(m)}
                      className="flex-1 py-2 text-sm font-bold border-2 transition-colors"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        backgroundColor: duration === m ? '#09090B' : '#FFFFFF',
                        color: duration === m ? '#FFFFFF' : '#09090B',
                        borderColor: duration === m ? '#09090B' : '#E4E4E7'
                      }}
                      data-testid={`duration-${m}`}
                    >
                      {m >= 60 ? `${m / 60}h` : `${m}m`}
                    </button>
                  ))}
                </div>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min={MIN_DURATION_MINUTES}
                  className="mt-2 border-2 focus:border-black"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                  data-testid="duration-input"
                />
              </div>
            </div>

            {/* Amount Preview */}
            <div className="p-4 mb-5 flex items-center justify-between" style={{ backgroundColor: '#F4F4F5' }}>
              <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                <Clock size={14} className="inline mr-1" /> {duration} min @ ₹{societyInfo.rate_per_hour}/hr
              </span>
              <span className="text-xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                ₹{amount.toFixed(0)}
              </span>
            </div>

            <Button
              onClick={handleVerifyAndBook}
              disabled={submitting}
              className="w-full p-4"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
              data-testid="proceed-to-pay-btn"
            >
              {submitting ? 'Processing...' : 'Proceed to Pay'}
              {!submitting && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && bookingData && (
          <div className="border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="step-payment">
            <h2 className="text-center font-bold text-lg mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Scan & Pay
            </h2>
            <p className="text-center text-xs mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
              Scan the QR code with any UPI app
            </p>

            <div className="text-center mb-5">
              <img
                src={bookingData.qr_code}
                alt="Payment QR"
                className="mx-auto mb-3"
                style={{ maxWidth: '220px', width: '100%' }}
                data-testid="payment-qr"
              />
              <p className="text-3xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                ₹{bookingData.amount.toFixed(0)}
              </p>
              <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                {vehicleNumber}
              </p>
            </div>

            <Button
              onClick={handleConfirmPayment}
              disabled={submitting}
              className="w-full p-4"
              style={{ backgroundColor: '#10B981', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold', fontSize: '16px' }}
              data-testid="confirm-payment-btn"
            >
              {submitting ? 'Confirming...' : "I've Paid"}
              {!submitting && <Check size={18} className="ml-2" />}
            </Button>
            <p className="text-[10px] mt-2 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>
              Parking spot will be allocated after payment confirmation
            </p>
          </div>
        )}

        {/* Step 5: Done */}
        {step === 5 && passData && (
          <div data-testid="step-done">
            <div className="border-2 p-6 mb-4 text-center" style={{ borderColor: '#10B981', backgroundColor: '#D1FAE5' }}>
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
                <Check size={28} style={{ color: '#FFFFFF' }} />
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#065F46' }}>
                You're Checked In
              </h2>
              <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#065F46' }}>
                Drive to your allocated parking spot
              </p>
            </div>

            <div className="border p-6 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
              <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your Parking Spot
              </p>
              <p
                className="text-6xl font-black mb-4"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}
                data-testid="allocated-spot"
              >
                {passData.spot_number}
              </p>
              <div className="flex justify-center gap-6 mb-4">
                <div>
                  <p className="text-[10px] uppercase font-bold" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>Vehicle</p>
                  <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{passData.vehicle_number}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>Exit By</p>
                  <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                    {new Date(passData.estimated_exit).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                </div>
              </div>

              <a
                href={passData.pass_link}
                className="block w-full p-3 text-center text-sm font-bold"
                style={{ backgroundColor: '#09090B', color: '#FBBF24', fontFamily: 'Cabinet Grotesk, sans-serif' }}
                data-testid="view-pass-link"
              >
                View Full Digital Pass
              </a>
              <p className="text-[10px] mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>
                You can extend your time from the digital pass
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorCheckIn;
